/**
 * Helpers for the {Session} total / {Session} only / Full Term subtotal
 * triplet used on the Dashboard, Cabins page, and Reports per the
 * Camp Highlands spec.
 *
 * Full Term means a camper is enrolled in *both* 1st Term and 2nd Term
 * for the same year — surfaced as a derived attribute, not a session.
 *
 * Definitions used here:
 *   - `total`     = number of campers attending the active session.
 *   - `fullTerm`  = subset of `total` that is also Full Term for the year.
 *   - `sessionOnly` = total - fullTerm. The camper is here this session
 *                     but not also in the *other* standard term.
 *
 * For the Rangers session there is no Full-Term split — Rangers is its
 * own session and isn't part of the 1st/2nd Term Full-Term derivation.
 */
import type { SessionLabel } from "../context/SeasonContext";

export interface SessionTotals {
  total: number;
  sessionOnly: number;
  fullTerm: number;
}

interface FullTermish {
  isFullTerm?: boolean | null;
}

export function computeSessionTotals(
  campers: readonly FullTermish[],
): SessionTotals {
  const total = campers.length;
  const fullTerm = campers.reduce((n, c) => n + (c.isFullTerm ? 1 : 0), 0);
  return {
    total,
    fullTerm,
    sessionOnly: Math.max(0, total - fullTerm),
  };
}

export const SESSION_HEADER_LABEL: Record<SessionLabel, string> = {
  first: "1st Term",
  second: "2nd Term",
  rangers: "Rangers",
};

/**
 * Format the canonical subtotal string for a session.
 *
 *   "1st Term: 187 (1st Term only: 92 · Full Term: 95)"
 *
 * For Rangers, we drop the breakdown and just return "Rangers: N".
 */
export function formatSessionTotalsLine(
  session: SessionLabel,
  totals: SessionTotals,
): string {
  const label = SESSION_HEADER_LABEL[session];
  if (session === "rangers") return `${label}: ${totals.total}`;
  return `${label}: ${totals.total} (${label} only: ${totals.sessionOnly} · Full Term: ${totals.fullTerm})`;
}
